import apiUtil, { throwApiError } from '~/utils/apiUtils'

const { get, post, patch } = apiUtil()

interface PricingTier {
  minUsers: number
  maxUsers: number
  monthlyPriceId: string
  annualPriceId: string
  name: string
}

interface CheckoutSessionRequest {
  email: string
  userCount: number
  isAnnual: boolean
  // Optional: pins the checkout to a specific org (users can belong to
  // several). Backend requires active billing ownership before using it.
  orgId?: string
}

interface CheckoutSessionResponse {
  transactionId: string
  priceId: string
  planName: string
  chargesImmediately: boolean
}

interface PaddleConfig {
  env: 'sandbox' | 'production'
  clientToken: string
}

interface CardlessTrialRequest {
  email: string
  userCount: number
  isAnnual: boolean
  countryCode: string
  postalCode: string
}

interface CardlessTrialResponse {
  subscriptionId: string
  customerId: string
  status: string
  origin: string
  trialEndsAt: string
  planName: string
}

interface PortalSessionResponse {
  id: string
  customerId: string
  urls: {
    overview: string
  }
  subscriptions: Array<{
    id: string
    cancelSubscription: string
    updateSubscriptionPaymentMethod: string
  }>
  createdAt: string
}

interface PaymentMethodTransactionResponse {
  transactionId: string
}

// Saved card on file for a customer. Null when none is saved (e.g. an active cardless trial).
export interface PaymentMethodResponse {
  type: string
  brand?: string
  last4?: string
  expiryMonth?: number
  expiryYear?: number
}

// One billing-history row. grandTotal is a Paddle minor-unit string (e.g. "29900").
export interface TransactionSummary {
  id: string
  status: string
  origin: string
  billedAt?: string
  currencyCode: string
  grandTotal: string
  invoiceNumber?: string
  invoiceAvailable: boolean
}

// Editable billing profile for a customer (contact, address, tax id).
export interface BillingDetails {
  customerId: string
  email: string
  name: string
  addressId?: string
  countryCode?: string
  postalCode?: string
  city?: string
  line1?: string
  region?: string
  businessId?: string
  businessName?: string
  taxIdentifier?: string
}

export interface BillingDetailsUpdate {
  email?: string
  name?: string
  countryCode?: string
  postalCode?: string
  city?: string
  line1?: string
  region?: string
  taxIdentifier?: string
}

interface SubscriptionUpdateRequest {
  subscriptionId: string
  userCount: number
  isAnnual: boolean
  prorationBillingMode?: string
}

interface SubscriptionUpdateResponse {
  subscriptionId: string
  status: string
  billingCycle: string
  nextBilledAt: string
  planName: string
}

// Live, Paddle-sourced billing summary. Money amounts are minor-unit strings (e.g. "29900").
export interface SubscriptionSummaryResponse {
  status: string
  currencyCode: string
  subtotal: string
  discount: string
  grandTotal: string
  quantity: number
  billingInterval: string
  billingFrequency: number
  // "automatic" (card on file) or "manual" (invoiced)
  collectionMode: string
  trialRequiresPaymentMethod: boolean
  startedAt?: string
  firstBilledAt?: string
  nextBilledAt?: string
  currentPeriodStartsAt?: string
  currentPeriodEndsAt?: string
  // Paddle's scheduled_change ("cancel" while a cancellation is pending)
  scheduledChangeAction?: string
  scheduledChangeEffective?: string
  hasDiscount: boolean
  // True only for a live/trialing subscription with NO scheduled cancellation.
  autoRenew: boolean
  // Paddle source-of-truth trial end (only present for a trialing subscription).
  trialEndsAt?: string
  trialDaysRemaining?: number
}

class PaddleAPI {
  constructor() {
    this.createCheckoutSession = this.createCheckoutSession.bind(this)
    this.getPricingTiers = this.getPricingTiers.bind(this)
    this.getPaddleConfig = this.getPaddleConfig.bind(this)
    this.createCardlessTrial = this.createCardlessTrial.bind(this)
    this.createPortalSession = this.createPortalSession.bind(this)
    this.getPaymentMethodTransaction =
      this.getPaymentMethodTransaction.bind(this)
    this.getPaymentMethod = this.getPaymentMethod.bind(this)
    this.getTransactions = this.getTransactions.bind(this)
    this.getInvoiceUrl = this.getInvoiceUrl.bind(this)
    this.getBillingDetails = this.getBillingDetails.bind(this)
    this.updateBillingDetails = this.updateBillingDetails.bind(this)
    this.updateSubscription = this.updateSubscription.bind(this)
    this.getSubscriptionSummary = this.getSubscriptionSummary.bind(this)
  }

  /**
   * Get the live billing summary (price + discount) for a subscription from Paddle.
   */
  async getSubscriptionSummary(
    subscriptionId: string,
  ): Promise<SubscriptionSummaryResponse> {
    const response = await get(`/paddle/subscription/${subscriptionId}/summary`)
    if (!response.ok) {
      await throwApiError(response)
    }
    return await response.json()
  }

  /**
   * Get the saved payment method (card on file) for a customer. Resolves to null when none.
   */
  async getPaymentMethod(
    customerId: string,
  ): Promise<PaymentMethodResponse | null> {
    const response = await get(`/paddle/customer/${customerId}/payment-method`)
    if (!response.ok) {
      await throwApiError(response)
    }
    return await response.json()
  }

  /**
   * Get the billing history (transactions, most recent first) for a subscription.
   */
  async getTransactions(subscriptionId: string): Promise<TransactionSummary[]> {
    const response = await get(
      `/paddle/subscription/${subscriptionId}/transactions`,
    )
    if (!response.ok) {
      await throwApiError(response)
    }
    const json = await response.json()
    return json.transactions || []
  }

  /**
   * Get a signed PDF invoice URL for one of a subscription's transactions.
   */
  async getInvoiceUrl(
    subscriptionId: string,
    transactionId: string,
  ): Promise<string> {
    const response = await get(
      `/paddle/subscription/${subscriptionId}/transactions/${transactionId}/invoice`,
    )
    if (!response.ok) {
      await throwApiError(response)
    }
    const json = await response.json()
    return json.url
  }

  /**
   * Get the editable billing profile (contact, address, tax id) for a customer.
   */
  async getBillingDetails(customerId: string): Promise<BillingDetails> {
    const response = await get(`/paddle/customer/${customerId}/billing`)
    if (!response.ok) {
      await throwApiError(response)
    }
    return await response.json()
  }

  /**
   * Update the billing profile for a customer. Returns the refreshed details.
   */
  async updateBillingDetails(
    customerId: string,
    data: BillingDetailsUpdate,
  ): Promise<BillingDetails> {
    const response = await patch(`/paddle/customer/${customerId}/billing`, data)
    if (!response.ok) {
      await throwApiError(response)
    }
    return await response.json()
  }

  /**
   * Get Paddle configuration for frontend initialization
   * @returns Paddle environment and client token
   */
  async getPaddleConfig(): Promise<PaddleConfig> {
    try {
      const response = await get('/paddle/config')

      if (!response.ok) {
        await throwApiError(response)
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching Paddle config:', error)
      throw error
    }
  }

  /**
   * Create Paddle checkout session for paid signups
   * @param data - Checkout session request data
   * @returns Transaction ID and plan details
   */
  async createCheckoutSession(
    data: CheckoutSessionRequest,
  ): Promise<CheckoutSessionResponse> {
    try {
      const response = await post('/paddle/checkout', data)

      if (!response.ok) {
        await throwApiError(response)
      }

      return await response.json()
    } catch (error) {
      console.error('Error creating checkout session:', error)
      throw error
    }
  }

  /**
   * Create cardless trial subscription for free signups
   * @param data - Trial request data
   * @returns Subscription details
   */
  async createCardlessTrial(
    data: CardlessTrialRequest,
  ): Promise<CardlessTrialResponse> {
    try {
      const response = await post('/paddle/cardless-trial', data)

      if (!response.ok) {
        await throwApiError(response)
      }

      return await response.json()
    } catch (error) {
      console.error('Error creating cardless trial:', error)
      throw error
    }
  }

  /**
   * Create customer portal session for subscription management
   * @param subscriptionIds - Optional list of subscription IDs
   * @returns Portal session with authenticated URLs
   */
  async createPortalSession(
    subscriptionIds?: string[],
  ): Promise<PortalSessionResponse> {
    try {
      const response = await post('/paddle/portal-session', { subscriptionIds })

      if (!response.ok) {
        await throwApiError(response)
      }

      return await response.json()
    } catch (error) {
      console.error('Error creating portal session:', error)
      throw error
    }
  }

  /**
   * Get transaction for updating payment method on a subscription
   * @param subscriptionId - The subscription ID
   * @returns Transaction ID for payment method update
   */
  async getPaymentMethodTransaction(
    subscriptionId: string,
  ): Promise<PaymentMethodTransactionResponse> {
    try {
      const response = await get(
        `/paddle/update-payment-method/${subscriptionId}`,
      )

      if (!response.ok) {
        await throwApiError(response)
      }

      return await response.json()
    } catch (error) {
      console.error('Error getting payment method transaction:', error)
      throw error
    }
  }

  /**
   * Get available pricing tiers
   * @returns List of pricing tiers
   */
  async getPricingTiers(): Promise<{ tiers: PricingTier[] }> {
    try {
      const response = await get('/paddle/pricing')

      if (!response.ok) {
        await throwApiError(response)
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching pricing tiers:', error)
      throw error
    }
  }

  /**
   * Update an existing subscription (for upgrades/downgrades)
   * @param data - Subscription update request data
   * @returns Updated subscription details
   */
  async updateSubscription(
    data: SubscriptionUpdateRequest,
  ): Promise<SubscriptionUpdateResponse> {
    try {
      const response = await patch('/paddle/update-subscription', data)

      if (!response.ok) {
        await throwApiError(response)
      }

      return await response.json()
    } catch (error) {
      console.error('Error updating subscription:', error)
      throw error
    }
  }

  // Get plan/tier name for a seat count. Display amounts come live from Paddle (see usePaddle),
  // not from this file. Names mirror the backend 4-tier per-seat structure.
  getPlanName(userCount: number): string {
    if (userCount === 1) {
      return 'Individual (1 seat)'
    } else if (userCount >= 2 && userCount <= 5) {
      return 'Team (2-5 seats)'
    } else if (userCount >= 6 && userCount <= 10) {
      return 'Growth (6-10 seats)'
    } else if (userCount >= 11 && userCount <= 20) {
      return 'Scale (11-20 seats)'
    } else {
      return 'Custom Plan'
    }
  }
}

const paddleAPI = new PaddleAPI()

export default paddleAPI
